/**
 * Deterministic, seedable RNG utilities.
 *
 * World-level noise uses `rngForDay(seed, day)` so that the market's evolution
 * (oil path, demand noise, event rolls) is a pure function of the run seed and
 * the day index — independent of frame timing or how fast the player runs the
 * clock. This gives us reproducible runs and replay-friendly balancing.
 *
 * Player-driven randomness (PO arrival variance, etc.) also flows through
 * per-day streams so it stays deterministic within a save.
 */

/** 32-bit FNV-1a hash — cheap, well-distributed stream mixer. */
export function hash32(...parts: number[]): number {
  let h = 0x811c9dc5;
  for (const p of parts) {
    let x = p | 0;
    x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
    x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
    h = Math.imul(h ^ (x >>> 0), 0x01000193);
  }
  return h >>> 0;
}

/** Mulberry32 — tiny, high-quality PRNG returning floats in [0, 1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Deterministic RNG stream for a given (seed, day) pair plus an optional lane. */
export function rngForDay(seed: number, day: number, lane = 0): () => number {
  return mulberry32(hash32(seed, day, lane));
}

/** Approximate standard normal via Irwin–Hall (n=12) — fine for game noise. */
export function gauss(rng: () => number): number {
  let sum = -6;
  for (let i = 0; i < 12; i++) sum += rng();
  return sum; // ~N(0, 1)
}

export function pick<T>(rng: () => number, arr: readonly T[]): T {
  return arr[Math.floor(rng() * arr.length) % arr.length];
}

/** Random integer in [min, max] inclusive. */
export function randInt(rng: () => number, min: number, max: number): number {
  return min + Math.floor(rng() * (max - min + 1));
}
